import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Alert,
  Typography,
} from "@mui/material";
import { SERVER_URL } from "../constants";

// Lets the signed-in user change their own password. The backend identifies the
// user from the bearer token and verifies the current password before setting
// the new one (self-service endpoint, separate from the admin reset-password).
export default function ChangePasswordDialog({ open, onClose, onChanged }) {
  const email = sessionStorage.getItem("userEmail") || "";
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setSaving(false);
    setError("");
    setDone(false);
  };

  const handleClose = () => {
    if (saving) return;
    // After a successful change the backend revokes all tokens, so the current
    // session is dead — route to a fresh sign-in rather than back into the app.
    if (done) {
      reset();
      onChanged?.();
      return;
    }
    reset();
    onClose?.();
  };

  const handleSubmit = async () => {
    setError("");
    if (!currentPassword || !newPassword) {
      setError("Fill in all fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("The new passwords don't match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${SERVER_URL}api/v1/auth/change-password`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Failed to change password");
      }
      setDone(true);
    } catch (e) {
      setError(e.message || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Change password</DialogTitle>
      <DialogContent dividers>
        {done ? (
          <Alert severity="success">
            Your password has been changed. For security you've been signed out of
            all sessions — please sign in again with your new password.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              This is a shared login — changing it here also updates your
              Locatorbase password.
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Account"
              value={email}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Current password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              size="small"
              fullWidth
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Forgotten your current password? There's no automated reset — contact
              Alex or another Admin to have it reset.
            </Typography>
            <TextField
              label="New password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              size="small"
              fullWidth
            />
            <TextField
              label="Confirm new password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              size="small"
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {done ? (
          <Button variant="contained" onClick={handleClose}>
            Sign in again
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? "Saving…" : "Change password"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
